/* eslint-disable no-unused-vars */
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Box, Table, TableBody, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import type { ApiContract } from '@agunity/api-v4';

import AgIconButton from '../../stories/AgIconButton';
import { AgColor } from '../../stories/Styles/AgColor';

import { formatDate, mapToKeyValue } from '../../utils';
import { ReactComponent as RemoveIcon } from '../../stories/assets/delete.svg';
import type { DropdownOptions } from './types';
import * as S from './styled';

type ContractTable = {
  canEdit?: boolean;
  options?: Record<string, DropdownOptions[]>,
  contracts: ApiContract[];
  onEdit: (contract: ApiContract) => void;
  onDelete: (contract: ApiContract) => void;
}

const ContractTable = ({ canEdit, options, contracts = [], onEdit, onDelete }: ContractTable) => {
  const messages = useIntl().formatMessage;
  const contractTypes = useMemo(() => mapToKeyValue(options?.contractTypes || []), [options?.contractTypes]);

  const headers = [{
    field: 'type',
    headerName: messages({ id: 'CONTRACTS_HEADER_TYPE' }),
    width: 165,
  },
  {
    field: 'startDate',
    headerName: messages({ id: 'CONTRACTS_HEADER_START_DATE' }),
    width: 114,
  },
  {
    field: 'endDate',
    headerName: messages({ id: 'CONTRACTS_HEADER_END_DATE' }),
    width: 114,
  },
  {
    field: 'actions',
    headerName: '',
    width: 114,
  }];

  return (
    <TableContainer>
      <Table sx={{ borderTop: `1px solid ${AgColor.grey4}`, borderLeft: `1px solid ${AgColor.grey4}`, }}>
        <TableHead>
          <TableRow>
            {
              headers?.map((header) => (
                <S.StyledTableCell key={header.field} width={header.width}>{header.headerName || ''}</S.StyledTableCell>
              ))
            }
          </TableRow>
        </TableHead>
        <TableBody>
          {
            contracts?.map((contract, index) => (
              <TableRow key={index}>
                <S.StyledTableCell style={{ textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {contractTypes?.[contract?.contractType!]}
                    {contract.documents?.length !== 0
                      ? (
                        <Tooltip title={messages({ id: 'CONTRACT_TYPE_DOCUMENT_LENGTH' }, { length: (contract?.documents || []).length })}>
                          <InsertDriveFileOutlinedIcon fontSize='small' sx={{ color: AgColor.grey6, ml: 'auto' }} />
                        </Tooltip>
                      )
                      : null
                    }
                  </Box>
                </S.StyledTableCell>
                <S.StyledTableCell style={{ textAlign: 'left' }}>{formatDate(contract.startDate)}</S.StyledTableCell>
                <S.StyledTableCell style={{ textAlign: 'left' }}>{formatDate(contract.endDate)}</S.StyledTableCell>
                <S.StyledTableCell isAction>
                  <AgIconButton
                    style={{ display: 'inline-block', margin: '2px' }}
                    buttonStyle={{
                      borderColor: AgColor.grey0,
                      height: 44,
                      width: 44,
                      padding: 0
                    }}
                    onClick={() => onEdit(contract)}
                    icon={<OpenInNewOutlinedIcon style={{ color: AgColor.link1, height: 24, width: 24, }} />}
                  />
                  {canEdit ? (
                    <AgIconButton
                      style={{ display: 'inline-block', margin: '2px' }}
                      buttonStyle={{
                        borderColor: AgColor.grey0,
                        height: 44,
                        width: 44,
                        padding: 0
                      }}
                      onClick={() => onDelete(contract)}
                      icon={
                        <S.SVGContainer component='span' iconWidth={24} iconHeight={24} color={AgColor.errorMain}>
                          <RemoveIcon />
                        </S.SVGContainer>
                      }
                    />
                  ) : null}
                </S.StyledTableCell>
              </TableRow>
            ))
          }
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ContractTable;